import { Track } from '@cratehackers/api-client';
import ApiService from './Api.service';
import { cleanArtist, cleanTitle } from '../utils/cleanTrackMetadata';
import { formatMusicalKey } from '../utils/formatMusicalKey';

type KeyMatches = {
  perfectMatch?: string[];
  energyBoost?: string[];
  energyDrop?: string[];
  moodChange?: string[];
  compatible?: string[];
};

export type RecommendationKeyCategory =
  | 'PerfectMatch'
  | 'EnergyBoost'
  | 'EnergyDrop'
  | 'MoodChange'
  | 'Compatible';

const keyCategoryOrder: Record<RecommendationKeyCategory, number> = {
  PerfectMatch: 1,
  EnergyBoost: 2,
  EnergyDrop: 3,
  MoodChange: 4,
  Compatible: 5,
};

export const formatKeyCategory = (
  category: RecommendationKeyCategory,
): string => {
  if (category === 'PerfectMatch') return 'Perfect match';
  if (category === 'EnergyBoost') return 'Energy boost';
  if (category === 'EnergyDrop') return 'Energy drop';
  if (category === 'MoodChange') return 'Mood change';
  return 'Compatible';
};

const normalizeKeyMatches = (raw: object): KeyMatches => {
  const row = raw as Record<string, string[] | undefined>;
  return {
    perfectMatch: row.perfectMatch ?? row.PerfectMatch,
    energyBoost: row.energyBoost ?? row.EnergyBoost,
    energyDrop: row.energyDrop ?? row.EnergyDrop,
    moodChange: row.moodChange ?? row.MoodChange,
    compatible: row.compatible ?? row.Compatible,
  };
};

const getKeyCategory = (
  key: string,
  keyMatches: KeyMatches | null,
): RecommendationKeyCategory | null => {
  if (!keyMatches || !key) return null;
  for (const category of Object.keys(keyMatches) as (keyof KeyMatches)[]) {
    const keys = keyMatches[category];
    if (Array.isArray(keys) && keys.includes(key)) {
      const formatted = category.charAt(0).toUpperCase() + category.slice(1);
      return formatted as RecommendationKeyCategory;
    }
  }
  return null;
};

const artistName = (artists: Track['artists']): string => {
  if (!Array.isArray(artists)) return '';
  return artists
    .map((artist) =>
      typeof artist === 'string'
        ? artist
        : ((artist as { name?: string })?.name ?? ''),
    )
    .filter(Boolean)
    .join(', ');
};

const albumCover = (track: Track): string | null => {
  const album = track.album as { imageUrl?: string; image_url?: string };
  return album?.imageUrl ?? album?.image_url ?? null;
};

const isSameSong = (
  leftArtist: string,
  leftTitle: string,
  rightArtist: string,
  rightTitle: string,
): boolean => {
  const cleanLeftArtist = (cleanArtist(leftArtist) || '').toLowerCase();
  const cleanLeftTitle = (cleanTitle(leftTitle) || '').toLowerCase();
  const cleanRightArtist = (cleanArtist(rightArtist) || '').toLowerCase();
  const cleanRightTitle = (cleanTitle(rightTitle) || '').toLowerCase();
  return (
    cleanLeftArtist === cleanRightArtist && cleanLeftTitle === cleanRightTitle
  );
};

export type AgentRecommendation = {
  id: string;
  artist: string;
  title: string;
  bpm: number;
  key: string;
  albumCover: string | null;
  match: string | null;
  matchCategory: RecommendationKeyCategory | null;
};

export const getTrackRecommendations = async (term: string) => {
  const searchTerm = term.trim();
  if (!searchTerm) {
    return {
      available: false,
      message: 'term is required — use "Artist - Title".',
    };
  }

  try {
    const api = ApiService.getApiClient();
    const resp = await api.trackControllerRecommendationSearch({
      spotifySearchDto: { term: searchTerm },
    });

    if (!resp?.length) {
      return {
        available: true,
        seed: null,
        recommendations: [] as AgentRecommendation[],
        message: 'No tracks found for that search.',
      };
    }

    const first = resp[0];
    const seedKey = formatMusicalKey(first.keys?.camelot ?? first.key ?? '');
    let keyMatches: KeyMatches | null = null;

    if (seedKey) {
      try {
        const matches = await api.keysControllerGetKeyMatches({
          keyDto: { camelot: seedKey, standard: '' },
        });
        keyMatches = normalizeKeyMatches(matches as object);
      } catch {
        keyMatches = null;
      }
    }

    const seed = {
      id: first.id ?? '',
      artist: artistName(first.artists),
      title: first.name ?? '',
      bpm: Math.round(first.bpm ?? 0),
      key: seedKey,
      albumCover: albumCover(first),
    };

    const recommendations = resp
      .slice(1)
      .map((track) => {
        const artist = artistName(track.artists);
        const title = track.name ?? '';
        const key = formatMusicalKey(track.keys?.camelot ?? track.key ?? '');
        const matchCategory = getKeyCategory(key, keyMatches);
        return {
          id: track.id ?? '',
          artist,
          title,
          bpm: Math.round(track.bpm ?? 0),
          key,
          albumCover: albumCover(track),
          match: matchCategory ? formatKeyCategory(matchCategory) : null,
          matchCategory,
        };
      })
      .filter((track) => {
        if (!track.id || track.id === seed.id) return false;
        return !isSameSong(track.artist, track.title, seed.artist, seed.title);
      })
      .sort((left, right) => {
        const orderLeft = left.matchCategory
          ? keyCategoryOrder[left.matchCategory]
          : 6;
        const orderRight = right.matchCategory
          ? keyCategoryOrder[right.matchCategory]
          : 6;
        return orderLeft - orderRight;
      })
      .slice(0, 8);

    return {
      available: true,
      engine: 'crate-hackers-recommendation-search',
      seed,
      recommendations,
      note: 'Up to five rows in a Track|BPM|Key|Match table. One short line above.',
    };
  } catch (error) {
    return {
      available: false,
      message:
        error instanceof Error
          ? error.message
          : 'Could not fetch track recommendations.',
    };
  }
};
